/**
 * PaymentHelper.ts - Utility functions for payment processing
 * 
 * This file contains helper functions for payment processing,
 * including data validation, formatting, and backup mechanisms.
 */

import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// Standardized interface for payment details
export interface PaymentDetails {
  id: string;
  orderId?: string;
  paymentId?: string;
  amount: number;
  currency: string;
  planId: string;
  planName: string;
  billingCycle: string;
  status: string;
  timestamp: string;
  notes?: Record<string, any>;
}

// Track the most recent recovery attempt to prevent duplicate toasts
let lastRecoveryAttempt = 0;
const RECOVERY_COOLDOWN_MS = 5000; // 5 seconds cooldown between recovery attempts

/**
 * Flag to prevent multiple recovery attempts in the same session
 */
const RECOVERY_ATTEMPTED_KEY = 'subscription_recovery_attempted';

/**
 * Check if recovery has already been attempted in this session
 */
export const hasAttemptedRecovery = (): boolean => {
  try {
    return localStorage.getItem(RECOVERY_ATTEMPTED_KEY) === 'true';
  } catch (err) {
    return false;
  }
};

/**
 * Mark recovery as attempted to prevent duplicate attempts
 */
export const markRecoveryAttempted = () => {
  try {
    localStorage.setItem(RECOVERY_ATTEMPTED_KEY, 'true');
    console.log('Marked recovery as attempted');
  } catch (err) {
    console.error('Failed to mark recovery as attempted:', err);
  }
};

/**
 * Clear recovery attempted flag (used when logging out)
 */
export const clearRecoveryFlag = () => {
  try {
    localStorage.removeItem(RECOVERY_ATTEMPTED_KEY);
  } catch (err) {
    console.error('Failed to clear recovery flag:', err);
  }
};

/**
 * Stores essential payment data in local storage as backup
 */
export const storePaymentBackup = (paymentData: Partial<PaymentDetails>) => {
  try {
    const timestamp = new Date().toISOString();
    
    // Ensure we have minimum required fields
    const backupData = {
      ...paymentData,
      timestamp,
    };
    
    localStorage.setItem('lastSuccessfulPayment', JSON.stringify(backupData));
    console.log('Payment backup data stored successfully');
    return true;
  } catch (err) {
    console.error('Failed to store payment backup data:', err);
    return false;
  }
};

/**
 * Retrieves payment backup data from local storage
 */
export const getPaymentBackup = (): Partial<PaymentDetails> | null => {
  try {
    const storedData = localStorage.getItem('lastSuccessfulPayment');
    if (!storedData) return null;
    
    return JSON.parse(storedData) as Partial<PaymentDetails>;
  } catch (err) {
    console.error('Failed to retrieve payment backup data:', err);
    return null;
  }
};

/**
 * Clears the payment backup from localStorage
 */
export const clearPaymentBackup = (): boolean => {
  try {
    localStorage.removeItem('lastSuccessfulPayment');
    console.log('Payment backup data cleared');
    return true;
  } catch (err) {
    console.error('Failed to clear payment backup data:', err);
    return false;
  }
};

/**
 * Fetches payment details with retry logic
 */
export const fetchPaymentDetails = async (orderId: string, maxRetries = 3): Promise<PaymentDetails | null> => {
  let retries = 0;
  
  while (retries <= maxRetries) {
    try {
      // Check if we have a valid session before making the request
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        console.warn('No active session when fetching payment details');
        return null;
      }
      
      const { data, error } = await supabase.functions.invoke('get-razorpay-payment-details', {
        body: { orderId }
      });
      
      if (error) throw new Error(error.message);
      if (!data) throw new Error('No data returned');
      
      // Transform to standardized format
      return {
        id: data.id,
        orderId: data.id,
        paymentId: data.payment_id,
        amount: data.amount / 100, // Convert from paisa to rupees
        currency: data.currency || 'INR',
        planId: data.notes?.plan_id || '',
        planName: data.notes?.plan_name || 'Subscription',
        billingCycle: data.notes?.billing_cycle || 'monthly',
        status: data.status,
        timestamp: data.createdAt || new Date().toISOString(),
        notes: data.notes
      };
    } catch (err) {
      console.error(`Payment detail fetch attempt ${retries + 1}/${maxRetries + 1} failed:`, err);
      
      // If we've reached max retries, return null
      if (retries >= maxRetries) {
        return null;
      }
      
      // Otherwise wait before retrying (exponential backoff)
      const delay = Math.min(1000 * Math.pow(2, retries), 8000);
      await new Promise(resolve => setTimeout(resolve, delay));
      retries++;
    }
  }
  
  return null;
};

/**
 * Format currency amount for display
 */
export const formatCurrency = (amount: number, currency = 'INR'): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
  }).format(amount);
};

/**
 * Checks if the user has a valid authentication session
 * @returns boolean indicating if the user is authenticated
 */
const checkAuthStatus = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Auth status check error:', error);
      return false;
    }
    return !!data?.session;
  } catch (err) {
    console.error('Exception checking auth status:', err);
    return false;
  }
};

/**
 * Recovers subscription state from payment records
 * Can be used as a fallback when webhook fails
 */
export const recoverSubscriptionFromPayment = async (paymentId?: string, orderId?: string, showToast: boolean = true): Promise<boolean> => {
  try {
    // First check if user is authenticated
    const isAuthenticated = await checkAuthStatus();
    if (!isAuthenticated) {
      console.error('Cannot recover subscription - user not authenticated');
      return false;
    }

    // Check if there's already an active subscription (to prevent unnecessary API calls)
    try {
      const { data: subData } = await supabase.functions.invoke('check-razorpay-subscription', {});
      if (subData?.has_active_subscription) {
        console.log('User already has active subscription, skipping recovery');
        return true; // Consider this a success since user has what they need
      }
    } catch (err) {
      console.log('Error checking existing subscription, will try recovery anyway:', err);
    }
    
    // First check if we already attempted recovery in this session
    if (hasAttemptedRecovery()) {
      console.log('Skipping subscription recovery - already attempted in this session');
      return false;
    }
    
    // Implement cooldown to prevent multiple recovery attempts in quick succession
    const now = Date.now();
    if (now - lastRecoveryAttempt < RECOVERY_COOLDOWN_MS) {
      console.log('Skipping subscription recovery due to cooldown', { 
        lastAttempt: new Date(lastRecoveryAttempt).toISOString(),
        timeSinceLastAttempt: now - lastRecoveryAttempt
      });
      return false;
    }
    
    lastRecoveryAttempt = now;
    markRecoveryAttempted(); // Mark that we've attempted recovery
    
    console.log('Attempting to recover subscription from payment:', { paymentId, orderId });
    
    if (!paymentId && !orderId) {
      // Try to get backup from localStorage if no IDs provided
      const backup = getPaymentBackup();
      paymentId = backup?.paymentId;
      orderId = backup?.orderId;
      
      if (!paymentId && !orderId) {
        console.error('No payment identifiers available for recovery');
        return false;
      }
    }
    
    // Call recovery edge function
    const { data, error } = await supabase.functions.invoke('recover-subscription-from-payment', {
      body: { paymentId, orderId, showToast }
    });
    
    if (error) {
      console.error('Recovery function error:', error);
      // Don't show error toast here, it's better to fail silently for this operation
      return false;
    }
    
    console.log('Subscription recovery result:', data);
    
    // If recovery was successful, clear the backup to prevent re-processing
    if (data?.success) {
      clearPaymentBackup();
      if (showToast) {
        toast.success('Your subscription has been activated successfully!');
      }
      return true;
    }
    
    // Already had subscription case
    if (data?.message?.includes("already has an active subscription")) {
      console.log("User already has an active subscription");
      clearPaymentBackup(); // Clear backup as no recovery needed
      return true;
    }
    
    return false;
  } catch (err) {
    console.error('Error recovering subscription:', err);
    return false;
  }
};

/**
 * Check if a date string is still valid considering timezone differences
 * @param dateStr ISO date string to check
 * @returns boolean indicating if the date is still in the future
 */
export const isDateValid = (dateStr: string | null): boolean => {
  if (!dateStr) return false;
  
  try {
    const date = new Date(dateStr);
    const now = new Date();
    
    // Add a small buffer (1 hour) to handle minor timezone differences
    // This ensures we don't invalidate subscriptions prematurely
    const bufferMs = 60 * 60 * 1000; // 1 hour in milliseconds
    return (date.getTime() + bufferMs) > now.getTime();
  } catch (err) {
    console.error("Error parsing date:", err);
    return false;
  }
};

/**
 * Verifies subscription status with fallbacks
 * Returns subscription data if active, null otherwise
 */
export const verifySubscriptionStatus = async (skipRecovery = false): Promise<{
  has_active_subscription: boolean;
  subscription: any;
} | null> => {
  try {
    // Check if user is authenticated before proceeding
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      console.warn('No active session when verifying subscription status');
      return { has_active_subscription: false, subscription: null };
    }
    
    // First try the normal subscription check
    const { data: subscriptionData, error: subscriptionError } = await supabase.functions.invoke(
      'check-razorpay-subscription',
      {}
    );
    
    if (subscriptionError) {
      console.error('Error checking subscription:', subscriptionError);
      // Return a default value without showing an error to the user
      return { has_active_subscription: false, subscription: null };
    }
    
    console.log('Subscription check result:', subscriptionData);
    
    // If we have a valid active subscription, return it
    if (subscriptionData && subscriptionData.has_active_subscription) {
      return subscriptionData;
    }
    
    // If no active subscription found and we haven't disabled recovery,
    // check if we have a successful payment that wasn't processed
    if (!skipRecovery && !hasAttemptedRecovery()) {
      console.log('No active subscription found, checking for successful payment records...');
      const backup = getPaymentBackup();
      
      if (backup && (backup.paymentId || backup.orderId)) {
        console.log('Found payment backup, attempting recovery:', backup);
        // Mark attempted before recovery to avoid race conditions
        markRecoveryAttempted();
        
        const recovered = await recoverSubscriptionFromPayment(backup.paymentId, backup.orderId, false);
        
        if (recovered) {
          // Re-check subscription after recovery
          const { data: refreshedData, error: refreshError } = await supabase.functions.invoke(
            'check-razorpay-subscription',
            {}
          );
          
          if (!refreshError && refreshedData?.has_active_subscription) {
            console.log('Successfully recovered subscription:', refreshedData);
            // Don't show toast here, let the component handle it
            return refreshedData;
          }
        }
      }
    }
    
    // Return whatever data we have
    return subscriptionData || { has_active_subscription: false, subscription: null };
  } catch (err) {
    console.error('Error in verifySubscriptionStatus:', err);
    return { has_active_subscription: false, subscription: null };
  }
};

/**
 * Checks if a payment record exists and is valid
 */
export const validatePaymentRecord = async (paymentId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('payment_records')
      .select('*')
      .eq('payment_id', paymentId)
      .single();
    
    if (error) {
      console.error('Error validating payment record:', error);
      return false;
    }
    
    return !!data && data.status === 'captured';
  } catch (err) {
    console.error('Error in validatePaymentRecord:', err);
    return false;
  }
};

/**
 * Resets all payment and subscription related state
 * Use when logging out
 */
export const resetPaymentState = () => {
  try {
    clearPaymentBackup();
    clearRecoveryFlag();
    lastRecoveryAttempt = 0;
    console.log('Payment state reset successfully');
  } catch (err) {
    console.error('Error resetting payment state:', err);
  }
};
